import * as THREE from 'three';
import { Renderer } from './Renderer.js';
import { Camera } from './Camera.js';
import { Sky } from './Sky.js';
import { State, States } from './State.js';
import { HUD } from './ui/HUD.js';
import { Cards } from './ui/Cards.js';
import { StarField } from './stars/StarField.js';
import { Sirius } from './stars/Sirius.js';
import { BirthStar } from './stars/BirthStar.js';
import { createFamily } from './stars/family.js';
import { createHomePlanets } from './stars/homePlanets.js';
import { createEuropaAndJupiter } from './stars/europa.js';
import { createSnowboarding } from './stars/snowboarding.js';
import { createPulsar } from './stars/pulsar.js';
import { createWanderer } from './stars/wanderer.js';
import { createRadioStars } from './stars/radioStars.js';
import { createEasterEggs } from './stars/easterEggs.js';
import { BlackHole } from './stars/BlackHole.js';
import { WishingStarsSystem } from './stars/wishingStarsSystem.js';
import { WISHES, wishCardHTML } from './data/wishes.js';
import { createWhisperStars } from './stars/whisperStars.js';
import { WHISPERS } from './data/whispers.js';
import { Opening } from './sequences/Opening.js';
import { Finale } from './sequences/Finale.js';
// ShootingStar.js (formerly SnowboardingComet.js) is the streaking-object
// template, kept for future reuse as the wishing-stars (= shooting stars)
// system. Not currently instantiated.
import { siteData } from './data/siteData.js';

const DISCOVERIES_KEY = 'meghas-universe:discoveries';
const WISHES_KEY = 'meghas-universe:wishes-caught';
const WHISPERS_KEY = 'meghas-universe:whispers-caught';
const OPENING_SEEN_KEY = 'meghas-universe:opening-seen';

// Number of discoveries required before the Black Hole appears in the
// sky. The BH is the climax of the experience - it shouldn't be the
// first thing she clicks. After this many discoveries we reveal it
// and fire a one-time alert pointing her toward it.
const BH_REVEAL_THRESHOLD = 10;
const BH_ALERT_TEXT = 'Something massive just opened beneath you.';

/**
 * Game.js - singleton orchestrator.
 *
 * Owns the renderer, camera, sky, state machine, HUD, cards, raycaster,
 * and discoveries set. Runs the animation loop. Routes interaction events.
 */
export class Game {
  constructor({ hyg }) {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x000308);

    this.renderer = new Renderer();
    this.camera = new Camera();
    this.sky = new Sky(this.scene);
    this.state = new State();
    this.hud = new HUD();
    this.cards = new Cards();

    // The real birth sky - every star Megha could see from Karnataka, mag ≤ 6,
    // transformed to her actual horizontal coordinates from Bangalore at midnight
    // April 2, 1998 IST (= April 1 18:30 UTC).
    const birthDate = new Date('1998-04-01T18:30:00Z');
    this.starField = new StarField({
      hyg,
      birthplace: siteData.megha.birthplace,
      birthDate,
    });
    this.sky.add(this.starField);

    // Compute her birth star (closest cataloged star to exactly 28 light-years)
    const birthStar = this.starField.findClosestToDistance(28);
    console.log('Birth star (closest to 28 ly):', birthStar);

    // Discovery tracking - persists across sessions in localStorage
    this.discoveries = new Set();
    this.caughtWishes = new Set();
    this.caughtWhispers = new Set();
    this.loadDiscoveries();
    this.loadCaughtWishes();
    this.loadCaughtWhispers();
    this.hud.setDiscoveries(this.discoveries);
    this.hud.setCaughtWishes(this.caughtWishes, WISHES);
    this.hud.setCaughtWhispers(this.caughtWhispers, WHISPERS);
    this.hud.onResetRequested(() => this.resetDiscoveries());
    this.hud.onWishReread((wishId) => {
      const wish = WISHES.find((w) => w.id === wishId);
      if (wish) this.cards.show(wishCardHTML(wish));
    });
    this.hud.onHintRequested((id) => this.pulseStarForHint(id));

    // Raycaster for hover/click detection
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2(-2, -2); // off-screen until first move
    this.currentHover = null;

    // === Interactive stars ===
    // Shared click handler for all interactive stars
    const onStarClick = (star) => {
      this.cards.show(star.cardHTML());
      this.discover(star.discoveryId);
    };

    // Sirius / Jimmy - warm gold, the literal Dog Star
    const sirius = new Sirius({ starField: this.starField, onClick: onStarClick });
    this.sky.add(sirius);
    if (sirius.paw) this.sky.group.add(sirius.paw);

    // Birth Star (Zeta Tucanae) - pale sun-yellow, the Sun twin at 28 ly
    this.sky.add(new BirthStar({ starField: this.starField, onClick: onStarClick }));

    // Family Constellation - Father (steady), Mother (mass-transfer binary), Brother
    const family = createFamily({ onClick: onStarClick });
    for (const member of family.members) this.sky.add(member);
    this.sky.add(family.lines);
    this.sky.add(family.motherStream);

    // Three Home Planets - Kerala (childhood), Bangalore (college), Seattle (now)
    const homePlanets = createHomePlanets({ onClick: onStarClick });
    for (const planet of homePlanets) this.sky.add(planet);

    // Europa + Jupiter - Europa is placed at the real position of Jupiter
    // on April 2 1998 over Bangalore, with Jupiter as a non-interactive
    // marker right next to it. To find Europa, find Jupiter.
    const { europa, jupiter } = createEuropaAndJupiter({
      starField: this.starField,
      onClick: onStarClick,
    });
    if (europa) this.sky.add(europa);
    if (jupiter) this.sky.add(jupiter);

    // Snowboarding - stationary icy blue-white star (cold/ice themed)
    this.sky.add(createSnowboarding({ onClick: onStarClick }));

    // Pulsar - dance star with strong fast pulse (Kathak + salsa, rhythm)
    this.sky.add(createPulsar({ onClick: onStarClick }));

    // The Wanderer - single star representing wanderlust + her travel.
    // Replaces the earlier 6-dot Wandering Planets cluster (which felt
    // like a list pretending to be a constellation).
    this.sky.add(createWanderer({ onClick: onStarClick }));

    // Radio Stars - 7 stars for the 7 members of BTS, in the northeast sky
    const radio = createRadioStars({ onClick: onStarClick });
    for (const star of radio.stars) this.sky.add(star);
    this.sky.add(radio.lines);

    // Easter eggs - 8 hidden objects scattered across the sky.
    // 2 personal moments + 6 Project Hail Mary references. Small, dim,
    // unmarked. Discoverable by accident on multiple visits.
    const eggs = createEasterEggs({
      starField: this.starField,
      onClick: onStarClick,
    });
    for (const egg of eggs) this.sky.add(egg);

    // The Black Hole - visual centerpiece. Click triggers the finale.
    // Hidden until the visitor has made BH_REVEAL_THRESHOLD discoveries.
    this.blackHole = new BlackHole({
      onClick: () => this.startFinale(),
    });
    this.sky.add(this.blackHole);
    if (this.discoveries.size < BH_REVEAL_THRESHOLD) {
      this.blackHole.setHidden(true);
    }

    // Wishing Stars system - periodically spawns shooting stars carrying
    // wishes from Megha's people. Click to catch.
    this.wishingSystem = new WishingStarsSystem({
      wishes: WISHES,
      sky: this.sky,
      isCardOpen: () => this.cards.isOpen(),
      onCatch: (wish) => {
        this.cards.show(wishCardHTML(wish));
        this.catchWish(wish.id);
      },
    });
    this.wishingSystem.setCaughtWishes(this.caughtWishes);
    this.sky.add(this.wishingSystem);

    // Whisper stars - tiny dim stars carrying fun facts about her.
    // Hover (not click) to discover. Label appears at the bottom of the screen.
    // Pass in every already-placed interactable so whispers don't land on top
    // of planets, family stars, easter eggs, etc — and so they spread out
    // instead of forming dense unreadable clusters.
    const avoidPositions = this.sky.interactables.map((o) => o.position.clone());
    const whisperStars = createWhisperStars({
      onHover: (whisper) => this.hud.showWhisper(whisper.text),
      onUnhover: () => this.hud.hideWhisper(),
      onDiscover: (whisper) => this.discoverWhisper(whisper.id),
      avoidPositions,
    });
    for (const star of whisperStars) this.sky.add(star);

    this.bindInteractionEvents();
    window.addEventListener('resize', () => this.onResize());

    this.clock = new THREE.Clock();

    // === Opening sequence (first visit only) ===
    const hasSeenOpening = localStorage.getItem(OPENING_SEEN_KEY) === 'true';
    if (hasSeenOpening) {
      this.state.set(States.EXPLORING);
      this.showDragHint();
    } else {
      this.state.set(States.OPENING_SEQUENCE);
      this.camera.locked = true;
      this.opening = new Opening({
        onComplete: () => {
          this.camera.locked = false;
          this.camera.resetToCurrentPose();
          this.state.set(States.EXPLORING);
          localStorage.setItem(OPENING_SEEN_KEY, 'true');
          this.showDragHint();
        },
      });
      // Start the opening shortly after init so the scene is rendered first
      setTimeout(() => this.opening.start(), 100);
    }
  }

  showDragHint() {
    const hint = document.getElementById('drag-hint');
    if (hint) hint.classList.add('show');
  }

  bindInteractionEvents() {
    // Hover: track mouse, do raycast each frame
    window.addEventListener('mousemove', (e) => {
      this.mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
      this.mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    });

    // Click detection: distinguish click from drag by checking if mouse moved
    let downPos = null;
    window.addEventListener('mousedown', (e) => {
      downPos = { x: e.clientX, y: e.clientY };
    });
    window.addEventListener('mouseup', (e) => {
      if (!downPos) return;
      const dx = Math.abs(e.clientX - downPos.x);
      const dy = Math.abs(e.clientY - downPos.y);
      const isClick = dx < 5 && dy < 5;
      downPos = null;
      if (isClick && !this.cards.isOpen()) {
        this.handleClick();
      }
    });

    // Touch equivalents
    let touchDownPos = null;
    window.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        touchDownPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
    });
    window.addEventListener('touchend', (e) => {
      if (!touchDownPos || e.changedTouches.length !== 1) return;
      const t = e.changedTouches[0];
      const dx = Math.abs(t.clientX - touchDownPos.x);
      const dy = Math.abs(t.clientY - touchDownPos.y);
      const isTap = dx < 8 && dy < 8;
      if (isTap && !this.cards.isOpen()) {
        // Set mouse to tap position so raycast picks the right object
        this.mouse.x = (t.clientX / window.innerWidth) * 2 - 1;
        this.mouse.y = -(t.clientY / window.innerHeight) * 2 + 1;
        this.updateHover();
        this.handleClick();
      }
      touchDownPos = null;
    });
  }

  handleClick() {
    if (this.currentHover && this.currentHover.onClick) {
      this.currentHover.onClick();
    }
  }

  updateHover() {
    if (this.cards.isOpen()) {
      // Don't hover-detect when a card is open
      if (this.currentHover) {
        if (this.currentHover.onUnhover) this.currentHover.onUnhover();
        this.currentHover = null;
      }
      return;
    }
    // Ensure world matrices are up-to-date so the raycaster sees objects
    // at their correct world positions (matters for objects that are
    // children of a Group, like the home planets).
    this.scene.updateMatrixWorld(true);
    this.raycaster.setFromCamera(this.mouse, this.camera.three);
    const hits = this.raycaster.intersectObjects(this.sky.interactables, false);
    const newHover = hits.length > 0 ? hits[0].object.userData.star : null;
    if (newHover !== this.currentHover) {
      if (this.currentHover && this.currentHover.onUnhover) this.currentHover.onUnhover();
      if (newHover && newHover.onHover) newHover.onHover();
      this.currentHover = newHover;
    }
  }

  // === Discovery tracking ===
  loadDiscoveries() {
    try {
      const saved = localStorage.getItem(DISCOVERIES_KEY);
      if (saved) {
        this.discoveries = new Set(JSON.parse(saved));
      }
    } catch (e) {
      console.warn('Failed to load discoveries from localStorage:', e);
    }
  }

  saveDiscoveries() {
    try {
      localStorage.setItem(DISCOVERIES_KEY, JSON.stringify([...this.discoveries]));
    } catch (e) {
      console.warn('Failed to save discoveries:', e);
    }
  }

  discover(id) {
    if (!this.discoveries.has(id)) {
      this.discoveries.add(id);
      this.saveDiscoveries();
      this.hud.setDiscoveries(this.discoveries);
      this.hud.showDiscoveryFlash();
      console.log(`Discovered: ${id} (${this.discoveries.size} total)`);

      // First time crossing the threshold this session → reveal the BH
      // and fire the one-time alert. Guard with the BH's own visibility
      // so we don't re-fire on returning visits where it's already shown.
      if (
        this.discoveries.size >= BH_REVEAL_THRESHOLD
        && this.blackHole
        && !this.blackHole.mesh.visible
      ) {
        this.blackHole.setHidden(false);
        this.hud.showBlackHoleAlert(BH_ALERT_TEXT);
      }
    }
  }

  resetDiscoveries() {
    this.discoveries.clear();
    this.caughtWishes.clear();
    this.caughtWhispers.clear();
    localStorage.removeItem(DISCOVERIES_KEY);
    localStorage.removeItem(WISHES_KEY);
    localStorage.removeItem(WHISPERS_KEY);
    localStorage.removeItem(OPENING_SEEN_KEY);
    this.hud.setDiscoveries(this.discoveries);
    this.hud.setCaughtWishes(this.caughtWishes, WISHES);
    this.hud.setCaughtWhispers(this.caughtWhispers, WHISPERS);
    if (this.wishingSystem) this.wishingSystem.setCaughtWishes(this.caughtWishes);
    // Re-hide the black hole so the next playthrough rediscovers it
    if (this.blackHole) this.blackHole.setHidden(true);
    this.hud.hideBlackHoleAlert();
    this.hud._resetHints();
    this.hud.renderChecklist();
    console.log('Discoveries + wishes + whispers + hints + opening reset (refresh to replay opening)');
  }

  /**
   * Find the sky object with the given discoveryId and pulse it red
   * for the hint system.
   */
  pulseStarForHint(id) {
    for (const obj of this.sky.objects) {
      if (obj.discoveryId === id && typeof obj.startHint === 'function') {
        obj.startHint(15);
        return;
      }
    }
  }

  // === Wishes ===

  loadCaughtWishes() {
    try {
      const saved = localStorage.getItem(WISHES_KEY);
      if (saved) this.caughtWishes = new Set(JSON.parse(saved));
    } catch (e) {
      console.warn('Failed to load caught wishes:', e);
    }
  }

  saveCaughtWishes() {
    try {
      localStorage.setItem(WISHES_KEY, JSON.stringify([...this.caughtWishes]));
    } catch (e) {
      console.warn('Failed to save caught wishes:', e);
    }
  }

  catchWish(wishId) {
    if (!this.caughtWishes.has(wishId)) {
      this.caughtWishes.add(wishId);
      this.saveCaughtWishes();
      this.hud.setCaughtWishes(this.caughtWishes, WISHES);
      if (this.wishingSystem) this.wishingSystem.setCaughtWishes(this.caughtWishes);
      console.log(`Caught wish: ${wishId} (${this.caughtWishes.size} total)`);
    }
  }

  // === Whispers ===

  loadCaughtWhispers() {
    try {
      const saved = localStorage.getItem(WHISPERS_KEY);
      if (saved) this.caughtWhispers = new Set(JSON.parse(saved));
    } catch (e) {
      console.warn('Failed to load caught whispers:', e);
    }
  }

  saveCaughtWhispers() {
    try {
      localStorage.setItem(WHISPERS_KEY, JSON.stringify([...this.caughtWhispers]));
    } catch (e) {
      console.warn('Failed to save caught whispers:', e);
    }
  }

  discoverWhisper(id) {
    if (!this.caughtWhispers.has(id)) {
      this.caughtWhispers.add(id);
      this.saveCaughtWhispers();
      this.hud.setCaughtWhispers(this.caughtWhispers, WHISPERS);
    }
  }

  start() {
    this.animate();
  }

  animate = () => {
    requestAnimationFrame(this.animate);
    const dt = this.clock.getDelta();

    this.camera.update(dt);
    this.sky.update(dt);
    // Finale runs AFTER camera so it can override camera state
    if (this.finale) this.finale.update(dt);
    this.updateHover();

    this.renderer.render(this.scene, this.camera.three);
  };

  startFinale() {
    if (this.finale && this.finale.state !== 'idle' && this.finale.state !== 'done') return;
    this.state.set(States.DESCENDING);
    this.finale = new Finale({
      camera: this.camera,
      blackHole: this.blackHole,
      onComplete: () => {
        this.state.set(States.POST_FINALE);
        this.finale = null;
        // Reset camera target so the orientation matches its current pose
        this.camera.resetToCurrentPose();
      },
    });
    this.finale.start();
  }

  onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h);
    this.camera.onResize(w, h);
  }
}
